import type { User } from '../types';

export type AdminRole = 'ADMIN' | 'SUPER_ADMIN' | 'STAFF' | 'VIEWER';
export type Role = User['role'];

export const ADMIN_PANEL_ROLES: readonly AdminRole[] = ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'];

export const ROLE_LABELS: Record<Role, string> = {
  STUDENT: 'Student',
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
  STAFF: 'Hostel Staff',
  VIEWER: 'Viewer',
};

// ---------------------------------------------------------------------------
// Granular Permissions
// ---------------------------------------------------------------------------

export const PERMISSIONS = {
  // Hostels
  'hostels:view': 'View hostels & room details',
  'hostels:create': 'Create new hostels',
  'hostels:edit': 'Edit hostel details, blocks, rooms',
  'hostels:delete': 'Delete hostels',

  // Bookings
  'bookings:view': 'View booking queue',
  'bookings:approve': 'Approve or reject bookings',
  'bookings:create': 'Create bookings on behalf of students',
  'bookings:cancel': 'Cancel existing bookings',

  // Allocations
  'allocations:view': 'View bed allocations',
  'allocations:manage': 'Assign or reassign bed spaces',

  // Payments
  'payments:view': 'View payment records',
  'payments:verify': 'Verify or flag payments',
  'payments:export': 'Export payment data',

  // Bank Statements
  'bank-statements:view': 'View bank statements',
  'bank-statements:upload': 'Upload bank statements',
  'bank-statements:reconcile': 'Reconcile transactions',

  // Students
  'students:view': 'View student records',
  'students:edit': 'Edit student information',
  'students:import': 'Bulk import students',
  'students:export': 'Export student data',
  'students:blacklist': 'Manage blacklisted students',

  // Support
  'support:view': 'View support tickets',
  'support:respond': 'Respond to tickets',
  'support:close': 'Close or escalate tickets',

  // Reports
  'reports:view': 'View analytics & reports',
  'reports:export': 'Export reports',

  // Occupancy Grid
  'occupancy:view': 'View occupancy grid',

  // Settings
  'settings:view': 'View system settings',
  'settings:edit': 'Modify system settings',

  // Staff Management
  'staff:view': 'View staff list',
  'staff:invite': 'Invite new staff members',
  'staff:manage': 'Edit staff roles & hostel assignments',
  'staff:remove': 'Remove staff access',
} as const;

export type Permission = keyof typeof PERMISSIONS;

const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

const ADMIN_PERMISSIONS: Permission[] = ALL_PERMISSIONS.filter(
  (p) => p !== 'staff:remove',
);

const STAFF_PERMISSIONS: Permission[] = [
  'hostels:view',
  'bookings:view',
  'bookings:approve',
  'allocations:view',
  'allocations:manage',
  'payments:view',
  'payments:verify',
  'bank-statements:view',
  'students:view',
  'support:view',
  'support:respond',
  'reports:view',
  'occupancy:view',
];

const VIEWER_PERMISSIONS: Permission[] = [
  'hostels:view',
  'bookings:view',
  'allocations:view',
  'payments:view',
  'bank-statements:view',
  'students:view',
  'support:view',
  'reports:view',
  'occupancy:view',
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  SUPER_ADMIN: ALL_PERMISSIONS,
  ADMIN: ADMIN_PERMISSIONS,
  STAFF: STAFF_PERMISSIONS,
  VIEWER: VIEWER_PERMISSIONS,
  STUDENT: [],
};

export function getPermissionsForRole(role: Role): readonly Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

export function hasPermission(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return (ROLE_PERMISSIONS[role] ?? []).includes(permission);
}

export function hasAnyPermission(
  role: Role | null | undefined,
  permissions: Permission[],
): boolean {
  if (!role) return false;
  const rolePerms = ROLE_PERMISSIONS[role] ?? [];
  return permissions.some((p) => rolePerms.includes(p));
}

export function hasAllPermissions(
  role: Role | null | undefined,
  permissions: Permission[],
): boolean {
  if (!role) return false;
  const rolePerms = ROLE_PERMISSIONS[role] ?? [];
  return permissions.every((p) => rolePerms.includes(p));
}

// ---------------------------------------------------------------------------
// Route Access (kept for backward compat, now derived from permissions)
// ---------------------------------------------------------------------------

export const ROUTE_ROLES = {
  dashboard: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  students: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  blacklist: ['ADMIN', 'SUPER_ADMIN'] as const,
  studentImport: ['ADMIN', 'SUPER_ADMIN'] as const,
  facilities: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  bookingReviews: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  allocations: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  payments: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  bankStatements: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  supportTickets: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  settings: ['ADMIN', 'SUPER_ADMIN'] as const,
  staffAccess: ['ADMIN', 'SUPER_ADMIN'] as const,
  hostels: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  reports: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
  occupancyGrid: ['ADMIN', 'SUPER_ADMIN', 'STAFF', 'VIEWER'] as const,
};

export function isAdminPanelRole(role?: string | null): role is AdminRole {
  if (!role) return false;
  return (ADMIN_PANEL_ROLES as readonly string[]).includes(role);
}

export function hasRouteAccess(
  role: string | null | undefined,
  allowedRoles: readonly AdminRole[],
): boolean {
  if (!role) return false;
  return (allowedRoles as readonly string[]).includes(role);
}
